import BasePage from "../BasePage";
import { useLocation } from "react-router";
import { ActionIcon, Box, Button, Divider, Group, Skeleton, Stack, Table, Text, TextInput } from "@mantine/core";
import { coreApi } from "../../utils/CoreAPI";
import { notifications } from "@mantine/notifications";
import { IconBrandAndroid, IconBrandAppleFilled, IconBrandWindowsFilled, IconCheck, IconDeviceDesktop, IconEye, IconLogout, IconMail, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import Device from "../../interfaces/Device";
import { closeAllDialogs, openDialogBox } from "../../components/DialogBox";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";

const OffboardUserDialog = ({ employee }: { employee: any }) => {
    const handleOffboarding = async () => {
        closeAllDialogs();

        const notificationId = notifications.show({
            title: "Offboarding In Progress",
            message: "Please do not navigate away until this process is complete.",
            loading: true,
            withBorder: true,
            withCloseButton: false
        });

        const response = await coreApi.post(`/graph/users/${employee['id']}/offboard`)

        if (response.status !== 200) {
            notifications.update({
                id: notificationId,
                title: "Offboarding Failed",
                message: "Please check the server logs for more details.",
                loading: false,
                icon: <IconX size={"1.125rem"} />,
                color: "red",
                withCloseButton: true
            });

            return;
        }

        notifications.update({
            id: notificationId,
            title: "Offboarding Complete",
            message: `${employee['displayName']} has been successfully offboarded!`,
            loading: false,
            icon: <IconCheck size={"1.125rem"} />,
            color: "teal",
            withCloseButton: true
        });
    };

    return (
        <Stack>
            <Group justify="right" gap={6}>
                <Button onClick={closeAllDialogs} radius={12} h={34}>Cancel</Button>
                <Button className="unify-btn-primary" type="submit" onClick={handleOffboarding} radius={12} h={34}>Offboard user</Button>
            </Group>
        </Stack>
    );
};

const SendEmailDialog = ({ employee }: { employee: any }) => {
    const [passwordFieldType, setPasswordFieldType] = useState<string>("password");
    const [disableButtons, setDisableButtons] = useState<boolean>(false);

    const form = useForm({
        mode: "uncontrolled",
        initialValues: {
            to: '',
            firstName: employee.displayName ? employee.displayName.split(' ')[0] : null,
            loginEmail: employee.userPrincipalName ? employee.userPrincipalName : null,
            password: '',
            startDate: dayjs().format('YYYY-MM-DD'),
        },

        validate: {
            to: (value) => {
                if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email';
                return null;
            },

            firstName: (value) => {
                if (value.trim().length < 2) return 'First name too short';
                return null;
            },

            loginEmail: (value) => {
                if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email';
                return null;
            },

            password: (value) => {
                if (value.length < 1) return 'Password too short';
                return null;
            },
        },
    });

    const handleSendEmail = async (data: any) => {
        setDisableButtons(true);

        const response = await coreApi.post("/graph/mail/send-mail", data)

        console.log(response.error!);

        if (!response.error) {
            notifications.show({
                color: "green",
                icon: <IconCheck size={24} />,
                title: "Send E-Mail",
                message: "E-Mail has been successfully sent to the user",
                withBorder: true
            });
        }
        else {
            notifications.show({
                color: "red",
                icon: <IconX size={24} />,
                title: "Send E-Mail",
                message: response.error,
                withBorder: true
            })
        }

        form.reset();
        setDisableButtons(false);
        closeAllDialogs();
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSendEmail(values))}>
            <Stack>
                <TextInput
                    withAsterisk
                    label="Personal Email"
                    placeholder="janedoe@gmail.com"
                    description="This will be used to send the user their temporary login details."
                    key={form.key('to')}
                    {...form.getInputProps('to')}
                />

                <TextInput
                    withAsterisk
                    label="First Name"
                    placeholder="Jane"
                    key={form.key('firstName')}
                    {...form.getInputProps('firstName')}
                />

                <TextInput
                    withAsterisk
                    label="Login Email"
                    placeholder="j.doe@experiorheadoffice.ca"
                    key={form.key('loginEmail')}
                    {...form.getInputProps('loginEmail')}
                />
                <TextInput
                    withAsterisk
                    type={passwordFieldType}
                    label="Login Password"
                    placeholder="************"
                    key={form.key('password')}
                    {...form.getInputProps('password')}
                    rightSection={
                        <ActionIcon
                            className="unify-action-icon-subtle"
                            onMouseDown={() => setPasswordFieldType("text")}
                            onMouseUp={() => setPasswordFieldType("password")}
                            style={{
                                width: "16px",
                                height: "16px"
                            }}>
                            <IconEye size={"1.125rem"} />
                        </ActionIcon>
                    }
                />

                <DatePickerInput
                    withAsterisk
                    label="Start Date"
                    placeholder="Select user start date"
                    key={form.key('startDate')}
                    {...form.getInputProps('startDate')}
                />

                <Group justify="right" gap={6}>
                    <Button
                        onClick={closeAllDialogs}
                        disabled={disableButtons}
                        radius={12} h={34}
                    >
                            Cancel
                    </Button>
                    <Button
                        className="unify-btn-primary"
                        type="submit"
                        disabled={disableButtons}
                        radius={12} h={34}
                    >
                        Send e-mail
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};

function ManageEmployeePage() {
    const { state } = useLocation();
    const { employee } = state;
    const [deviceData, setDeviceData] = useState<Device[]>([]);
    const [devicesLoading, setDevicesLoading] = useState<boolean>(true);

    const renderDeviceIcon = (device: Device) => {
        switch (device.operatingSystem) {
            case "Windows":
                return <IconBrandWindowsFilled size="1.75rem" />;
            case "Android":
                return <IconBrandAndroid size="1.75rem" />;
            case "IPhone":
                return <IconBrandAppleFilled size="1.75rem" />;
            default:
                return <IconDeviceDesktop size="1.75rem" />;
        }
    }

    useEffect(() => {
        const getUserDevices = async () => {
            const response = await coreApi.get(`/graph/users/${employee.id}/owned-devices`);

            setDeviceData(response.data['value']);
            setDevicesLoading(false);
        };

        getUserDevices();
    }, [])

    return (
        <BasePage
            breadcrumbs={[
                { label: "Active Employees", previousPage: true },
                { label: employee.displayName }
            ]}
            fullWidth
        >
            <Group>
                <Button
                    className="unify-btn-subtle unify-border-0"
                    leftSection={<IconLogout size={"1.125rem"} />}
                    onClick={() => 
                        openDialogBox({
                            title: "Offboard user",
                            description: "User access will be revoked and multi-factor authentication methods reset. This action is irreversible.",
                            children: <OffboardUserDialog employee={employee} />
                        })}
                >
                    Offboard
                </Button>

                <Button
                    className="unify-btn-subtle unify-border-0"
                    leftSection={<IconMail size={"1.125rem"} />}
                    onClick={() => openDialogBox({ title: 'Send login credentials', children: <SendEmailDialog employee={employee} /> })}
                >
                    Send E-Mail
                </Button>
            </Group>

            <Divider mb="xs" />

            <Table variant="vertical">
                <Table.Tbody>
                    <Table.Tr>
                        <Table.Th w={150}>User Id</Table.Th>
                        <Table.Td>{employee.id}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Display Name</Table.Th>
                        <Table.Td>{employee.displayName}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>E-mail</Table.Th>
                        <Table.Td>{employee.userPrincipalName}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Th>Job Title</Table.Th>
                        <Table.Td>{employee.jobTitle}</Table.Td>
                    </Table.Tr>
                </Table.Tbody>
            </Table>

            <Divider label="Devices" my="sm" />

            <Skeleton display={devicesLoading ? "block" : "none"} h="2.25rem" mb="sm" />
            <Skeleton display={devicesLoading ? "block" : "none"} h={10} mb="sm" />
            <Skeleton display={devicesLoading ? "block" : "none"} h={10} mb="sm" />
            <Skeleton display={devicesLoading ? "block" : "none"} h={10} />
            <Group>
                {deviceData.length > 0 ? deviceData.map((device, key) => {
                    return (
                        <Box
                            key={key}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "150px",
                                width: "150px",
                                border: "1px solid var(--border-color)",
                                borderRadius: "8px",
                            }}>

                            {renderDeviceIcon(device)}
                            <Text truncate maw={120} size="0.825rem" c="var(--text-dimmed)" my="xs">{device.displayName}</Text>
                            <Text size="0.825rem" c="var(--text-dimmed)" mb="xs">{device.operatingSystem}</Text>
                            <Text size="0.825rem" c="var(--text-dimmed)">{device.deviceOwnership == null ? "Personal" : "Company"}</Text>
                        </Box>
                    );
                }) : <Text size="0.825rem" c="var(--text-dimmed)">No devices found</Text>}
            </Group>
        </BasePage>
    )
}

export default ManageEmployeePage;